import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { interval, Observable, of, throwError, forkJoin } from 'rxjs';
import { catchError, switchMap, takeWhile, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

// ─── Reports module types ───────────────────────────────────────────
// Public metadata + job shapes returned by /v1/reports/* endpoints. Kept
// alongside the HTTP methods below so the reports hub + drawers can import
// them from the same place they call the API.
export interface FilterFieldOption {
  value: string | number;
  label: string;
}

export interface FilterField {
  key: string;
  label: string;
  type: 'date' | 'date-range' | 'text' | 'select' | 'multi-select' | 'boolean' | 'number';
  required?: boolean;
  options?: FilterFieldOption[];
  placeholder?: string;
  defaultValue?: any;
  helpText?: string;
  // Marker: options are populated client-side (per-shop data not known to the backend schema).
  // 'CHANNEL_PARTNERS' → the run-report drawer appends the shop's channel partners.
  optionsSource?: string;
}

export interface ReportMeta {
  key: string;
  title: string;
  category: string;
  description: string;
  icon?: string;
  defaultFormat: string;
  supportedFormats: string[];
  maxRows?: number;
  filtersSchema: FilterField[];
  requiredPermission?: string;
  canRun?: boolean;
}

export interface ReportJob {
  _id: string;
  backgroundJobId: string;
  backgroundJobType: string;
  status: 'QUEUED' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILED' | 'EXPIRED';
  error?: { code?: string; description?: string };
  data?: {
    path?: string;
    filename?: string;
    contentType?: string;
    format?: string;
    reportKey?: string;
    reportTitle?: string;
    sizeBytes?: number;
    generatedAt?: string;
    filters?: Record<string, any>;
  };
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  canDownload?: boolean;
  // Short-lived, token-gated download path returned by the API, relative to the
  // versioned base. Use this — NOT `data.path`. `data.path` is the object's own
  // URL in the bucket, and the buckets no longer allow anonymous reads outside
  // images/media, so opening it directly gets a 403.
  downloadPath?: string;
}

@Injectable({
  providedIn: 'root'
})
export class IntegrationService {
  private integrationService = environment.integrationService + '/v1';
  private loader = new HttpHeaders().set('noLoader', 'true');

  constructor(private http: HttpClient) {}

  getActivitiesByCriteria(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/activity-tracker/all?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Plugin APIs

  createPlugin(payload: any): Observable<any> {
    return this.http.post<any>(this.integrationService + '/plugin', payload).pipe(catchError((err) => throwError(err)));
  }

  getPlugin(payload: any, page: number, pageSize: number): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/plugin/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deletePlugin(deleteId: any): Observable<any> {
    return this.http
      .delete<any>(this.integrationService + '/plugin/' + deleteId)
      .pipe(catchError((err) => throwError(err)));
  }

  getPluginById(editId: any): Observable<any> {
    return this.http.get<any>(this.integrationService + '/plugin/' + editId).pipe(catchError((err) => throwError(err)));
  }

  updatePlugin(id: any, payload: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + '/plugin/' + id, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  checkPluginExists(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/plugin/check-exists', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getPluginCount(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/plugin/total', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Plugin Tracker APIs
  createPluginTracker(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/plugin-tracker', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  updatePluginTracker(payload: any, id: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + '/plugin-tracker/' + id, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deletePluginTracker(id: any): Observable<any> {
    return this.http
      .delete<any>(this.integrationService + '/plugin-tracker/' + id)
      .pipe(catchError((err) => throwError(err)));
  }

  getPluginTrackerById(id: any): Observable<any> {
    return this.http
      .get<any>(this.integrationService + '/plugin-tracker/' + id)
      .pipe(catchError((err) => throwError(err)));
  }

  getPluginTrackers(payload: any, page: number, pageSize: number): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/plugin-tracker/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  createBulkPluginTrackers(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/plugin-tracker/create-bulk`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Page APIs

  duplicateCheck(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/page/check-slug-title', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  createPage(payload: any): Observable<any> {
    return this.http.post<any>(this.integrationService + '/page', payload).pipe(catchError((err) => throwError(err)));
  }

  getPageData(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/page/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deletePage(deleteId: any): Observable<any> {
    return this.http
      .delete<any>(this.integrationService + '/page/' + deleteId)
      .pipe(catchError((err) => throwError(err)));
  }

  getPageById(id: any): Observable<any> {
    return this.http.get<any>(this.integrationService + '/page/' + id).pipe(catchError((err) => throwError(err)));
  }

  updatePage(payload: any, id: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + '/page/' + id, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Page APIs

  dublicateBlog(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/blog/check-slug-title', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  createBlog(payload: any): Observable<any> {
    return this.http.post<any>(this.integrationService + '/blog', payload).pipe(catchError((err) => throwError(err)));
  }

  getBlogData(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/blog/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deleteBlog(deleteId: any, shopId: any): Observable<any> {
    return this.http
      .delete<any>(this.integrationService + '/blog/' + deleteId + `?shop_id=${shopId}`)
      .pipe(catchError((err) => throwError(err)));
  }

  getBlogById(id: string): Observable<any> {
    return this.http.get<any>(`${this.integrationService}/blog/${id}`).pipe(catchError(() => of(null)));
  }

  getBlogsByIds(ids: string[]): Observable<any[]> {
    if (!ids || !ids.length) {
      return of([]);
    }

    const requests = ids.map((id) => this.getBlogById(id));
    return forkJoin(requests).pipe(map((blogs: any[]) => blogs.filter((b) => b !== null)));
  }

  updateBlog(payload: any, id: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + '/blog/' + id, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Marketing APIs

  createTemplate(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/message-template', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  updateTemplate(payload: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + `/message-template/${payload._id}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getTemplateById(id: any): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/message-template/${id}`)
      .pipe(catchError((err) => throwError(err)));
  }
  getTemplateByCriteria(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/message-template/criteria?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getWhatsappTemplates(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/whatsapp/get-templates?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  createCampaign(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/campaign', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getCampaignById(id: any): Observable<any> {
    return this.http.get<any>(this.integrationService + `/campaign/${id}`).pipe(catchError((err) => throwError(err)));
  }

  getCampaignByCriteria(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/campaign/all?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getCampaignMetaTags(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/campaign-meta-tags/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  sendCampaign(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/campaign/send-campaign`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  sendTestWhatsappCampaign(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/whatsapp/send-msg`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  createWhatsappCampaign(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/campaign`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  sendBulkEmail(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/email/send-bulk-email`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  sendEmail(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/email/send-email`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getCampaignAnalytics(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/notification-aggregate/campaign-analytics`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // senders details

  getSendersDetails(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/integration/criteria`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Notification

  getNotification(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/notification/all?page=${page}&pageSize=${pageSize}`, payload, {
        headers: this.loader
      })
      .pipe(catchError((err) => throwError(err)));
  }

  updateNotificationStatus(payload: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + `/notification/mark-read`, payload, {
        headers: this.loader
      })
      .pipe(catchError((err) => throwError(err)));
  }

  updateNotificationReadStatus(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/notification/read-all`, payload, {
        headers: this.loader
      })
      .pipe(catchError((err) => throwError(err)));
  }

  // Footer
  getFooterById(id: any): Observable<any> {
    return this.http.get<any>(this.integrationService + `/footer/${id}`).pipe(catchError((err) => throwError(err)));
  }

  createFooter(payload: any): Observable<any> {
    return this.http.post<any>(this.integrationService + '/footer', payload).pipe(catchError((err) => throwError(err)));
  }

  updateFooter(id: any, payload: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + `/footer/${id}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getFooterByShopId(payload: any, page: number, pageSize: number): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/footer/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Page Builder APIs

  getPageBuilder(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/page-builder/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }
  createPageBuilder(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/page-builder', payload)
      .pipe(catchError((err) => throwError(err)));
  }
  updatePageBuilder(payload: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + `/page-builder`, payload)
      .pipe(catchError((err) => throwError(err)));
  }
  // Remove one page-builder doc (used to put bundle pages back on the Product Details section).
  deletePageBuilder(id: string): Observable<any> {
    return this.http
      .delete<any>(this.integrationService + `/page-builder/${id}`)
      .pipe(catchError((err) => throwError(err)));
  }
  // CATEGORY templates: list Default + named templates (with their assigned category_ids) for a shop+device.
  listCategoryTemplates(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/page-builder/category-templates`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  //QLESS AI
  generate(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/ai/generate`, payload)
      .pipe(catchError((err) => throwError(err)));
  }
  uploadFile(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/file/upload-file`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // ── Media Manager ──────────────────────────────────────────────────────────
  // Folders (modules) with live-asset counts.
  getMediaModules(): Observable<any> {
    return this.http.get<any>(this.integrationService + `/media/modules`).pipe(catchError((err) => throwError(err)));
  }

  // Paginated asset list. `filters` may carry { module, status, search }.
  getMediaList(filters: any = {}, page = 1, pageSize = 30): Observable<any> {
    let params = `page=${page}&pageSize=${pageSize}`;
    if (filters.module) params += `&module=${encodeURIComponent(filters.module)}`;
    if (filters.status) params += `&status=${encodeURIComponent(filters.status)}`;
    if (filters.search) params += `&search=${encodeURIComponent(filters.search)}`;
    return this.http.get<any>(this.integrationService + `/media?${params}`).pipe(catchError((err) => throwError(err)));
  }

  // Single-file upload into the catalog folder. `payload` is a FormData (file, module).
  uploadMedia(payload: FormData): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/media/upload`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Scan the shop's catalog/ folder in storage and (re)index every image.
  scanMediaCatalog(): Observable<any> {
    return this.http.post<any>(this.integrationService + `/media/scan`, {}).pipe(catchError((err) => throwError(err)));
  }

  // Auto-link unlinked catalog files by filename → slug/SKU convention.
  autoLinkMedia(): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/media/auto-link`, {})
      .pipe(catchError((err) => throwError(err)));
  }

  // Link catalog files per an uploaded manifest CSV. `payload` = FormData (file).
  linkMediaManifest(payload: FormData): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/media/link-manifest`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Bulk zip upload. `payload` is a FormData (file, mode?). Returns a backgroundJobId to poll.
  uploadMediaZip(payload: FormData): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/media/upload-zip`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Poll a bulk-upload job by its backgroundJobId (status + summary in `data`).
  getMediaJob(backgroundJobId: string): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/background-job/job-id/${backgroundJobId}`)
      .pipe(catchError((err) => throwError(err)));
  }

  deleteMedia(id: string): Observable<any> {
    return this.http.delete<any>(this.integrationService + `/media/${id}`).pipe(catchError((err) => throwError(err)));
  }

  attachMedia(id: string, payload: any = {}): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/media/${id}/attach`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  detachMedia(id: string, target: any = {}): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/media/${id}/detach`, target)
      .pipe(catchError((err) => throwError(err)));
  }

  // Set a resource's gallery to an explicit ordered image list (rearrange modal).
  linkMedia(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/media/link`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Download the manifest.csv template (file column pre-filled) as text.
  downloadManifestTemplate(): Observable<string> {
    return this.http
      .get(this.integrationService + `/media/manifest-template`, { responseType: 'text' })
      .pipe(catchError((err) => throwError(err)));
  }

  // Read a resource's current images to seed the rearrange editor.
  getResourceImages(params: any): Observable<any> {
    let qs = new HttpParams();
    Object.keys(params || {}).forEach((k) => {
      if (params[k] != null && params[k] !== '') qs = qs.set(k, params[k]);
    });
    return this.http
      .get<any>(this.integrationService + `/media/resource/images`, { params: qs })
      .pipe(catchError((err) => throwError(err)));
  }

  // Content Management
  getContentManagement(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/content-management/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  createContentManagement(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/content-management', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  updateContentManagement(payload: any, id: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + `/content-management/${id}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deleteContentManagement(id: any): Observable<any> {
    return this.http
      .delete<any>(this.integrationService + `/content-management/${id}`)
      .pipe(catchError((err) => throwError(err)));
  }

  getContentManagementById(id: any): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/content-management/${id}`)
      .pipe(catchError((err) => throwError(err)));
  }

  // Batch fetch many content blocks in ONE call (replaces N× getContentManagementById).
  getContentManagementByIds(ids: string[]): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/content-management/by-ids', { ids })
      .pipe(catchError((err) => throwError(err)));
  }

  // template store and customizatin apis

  // Full theme (template-store) doc by id — includes default_cms + default_page_builder (the bundled design).
  getTemplateStoreById(id: any): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/template-store/${id}`)
      .pipe(catchError((err) => throwError(err)));
  }
  getTemplateList(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/template-store/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Create a new theme (TemplateStore). payload: { template_name, template_category, is_socommerz,
  // thumbnail, default_style, default_cms, default_page_builder }.
  createThemeTemplate(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/template-store`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Full re-theme of an existing shop: overwrites colours + advance customization AND replaces the
  // shop's CMS sections + Page Builder layout from the chosen theme. payload: { shop_id, template_id }.
  applyThemeToShop(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/template-store/apply-to-shop`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getTemplateCustomization(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/template-customization/get`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  updateTemplateCustomization(payload: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + `/template-customization/${payload._id}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Create the shop's customization row (used when none exists yet — e.g. a shop onboarded before
  // template-customization was provisioned, so Customize has no _id to PUT against).
  createTemplateCustomization(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/template-customization`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Integration

  addIntegration(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/integration`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  updateIntegration(payload: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + `/integration/${payload._id}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getIntegrationByCriteria(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/integration/criteria`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getIntegrationByShopId(shopId: any): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/integration/unique/${shopId}`)
      .pipe(catchError((err) => throwError(err)));
  }

  // Admin-only, owning-shop bound. Unlike /criteria this returns the UNREDACTED config
  // (real secret values) so the owner can view/edit a saved credential (e.g. TruePOS password).
  getIntegrationById(id: any): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/integration/${id}`)
      .pipe(catchError((err) => throwError(err)));
  }

  // dashboard overview
  getFunnelData(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/analytics/funnel-data`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getTotalCampaigns(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/campaign/total`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deleteIntegrationById(id: any): Observable<any> {
    return this.http
      .delete<any>(this.integrationService + `/integration/${id}`)
      .pipe(catchError((err) => throwError(err)));
  }
  deleteIntegrationByCriteria(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/integration/delete-by-criteria`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // ************* Annoucement Bar ****************

  getAnnouncementBar(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/announcement-bar/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getAnnouncementBarById(id: any): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/announcement-bar/${id}`)
      .pipe(catchError((err) => throwError(err)));
  }

  createAnnouncementBar(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/announcement-bar`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  updateAnnouncementBar(payload: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + `/announcement-bar/${payload._id}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deleteAnnouncementBar(id: any): Observable<any> {
    return this.http
      .delete<any>(this.integrationService + `/announcement-bar/${id}`, {})
      .pipe(catchError((err) => throwError(err)));
  }

  //---------- Customer Support ----------------

  createTicket(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/support-ticket`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getTickets(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/support-ticket/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  updateTicket(payload: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + `/support-ticket/${payload._id}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deleteTicket(id: any): Observable<any> {
    return this.http
      .delete<any>(this.integrationService + `/support-ticket/${id}`, {})
      .pipe(catchError((err) => throwError(err)));
  }

  getTicketById(id: any): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/support-ticket/${id}`)
      .pipe(catchError((err) => throwError(err)));
  }

  getTicketsCount(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/support-ticket/status-count`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  //-------------- Navbar------------------

  getNavbarByShopId(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/navbar/get-nested`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  createNavbar(payload: any): Observable<any> {
    return this.http.post<any>(this.integrationService + `/navbar`, payload).pipe(catchError((err) => throwError(err)));
  }

  updateNavbar(payload: any, id: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + `/navbar/${id}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // ---- Channel Partner overrides: revert a channel section back to inheriting the shop default ----
  revertChannelFooter(payload: { shop_id: any; channel_id: any }): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/footer/channel-override/revert`, payload)
      .pipe(catchError((err) => throwError(err)));
  }
  revertChannelNavbar(payload: { shop_id: any; channel_id: any }): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/navbar/channel-override/revert`, payload)
      .pipe(catchError((err) => throwError(err)));
  }
  revertChannelPageBuilder(payload: { shop_id: any; channel_id: any }): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/page-builder/channel-override/revert`, payload)
      .pipe(catchError((err) => throwError(err)));
  }
  revertChannelAnnouncementBar(payload: { shop_id: any; channel_id: any }): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/announcement-bar/channel-override/revert`, payload)
      .pipe(catchError((err) => throwError(err)));
  }
  revertChannelTemplate(payload: { shop_id: any; channel_id: any }): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/template-customization/channel-override/revert`, payload)
      .pipe(catchError((err) => throwError(err)));
  }
  // Ensure a channel's HOME layout includes a section (after a channel design override is saved), so
  // the edited component appears on the channel page at its shop-equivalent position.
  ensureChannelPageBuilderSection(payload: { shop_id: any; channel_id: any; base_id: any }): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/page-builder/channel-override/ensure-section`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Per-section override status (Inherited vs Customised) in one cheap call → { navbar, footer, pageBuilder }.
  getCmsOverrideStatus(payload: { shop_id: any; channel_id: any }): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/channel-override/status`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  //----------- Dashboard realtime insights------------

  getTotalUserAnalytics(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/analytics/total-users`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getNewUserAnalytics(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/analytics/new-returning`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getAvgBounceRate(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/analytics/bounce-rate`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getUserByRegion(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/analytics/user-by-region`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getUserByDevice(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/analytics/user-by-device`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getAvgSession(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/analytics/avg-session`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getTotalSession(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/analytics/total-session`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getTotalViews(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/analytics/total-views`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getUserByOS(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/analytics/user-by-os`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getViewsPerSession(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/analytics/views-per-session`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getViewsByPages(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/analytics/views-by-path`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  ///****************       Generative ai API     *********** */
  getCategoryDescriptionAi(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/ai/category-description`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getProductDescriptionAi(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/ai/product-description`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  aiGeneratedContent(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/ai/ai-generated-content`, payload)
      .pipe(catchError((err) => throwError(err)));
  }
  getBackGroundJob(payload: any): Observable<any> {
    return this.http.post<any>(this.integrationService + `/background-job`, payload);
  }
  importJob(payload: any): Observable<any> {
    return this.http.post<any>(this.integrationService + `/import-job`, payload);
  }

  getImportJobStatus(id: string): Observable<any> {
    return this.http.get<any>(this.integrationService + `/import-job/${id}`);
  }

  /**
   * Generic Import V2 client. The `type` parameter selects which import family
   * (product / inventory / customer / collection / …) the request targets.
   * The backend resolves `type` against its IMPORT_TYPES registry.
   */
  downloadImportTemplateV2(type: string, locatorType?: string, channel?: boolean): Observable<Blob> {
    // shop-locator only: locatorType picks the STORE vs SERVICE_CENTER template.
    // inventory only: channel=true drops Price/MRP for a channel import.
    const params: any = {};
    if (locatorType) params.locator_type = locatorType;
    if (channel) params.channel = 'true';
    return this.http.get(this.integrationService + `/import-v2/${type}/template`, {
      responseType: 'blob',
      ...(Object.keys(params).length ? { params } : {})
    });
  }

  uploadImportV2(type: string, payload: FormData): Observable<any> {
    return this.http.post<any>(this.integrationService + `/import-v2/${type}`, payload);
  }

  listActiveImportJobsV2(type: string, shopId: string): Observable<any> {
    return this.http.get<any>(this.integrationService + `/import-v2/${type}/active`, {
      params: { shopId }
    });
  }

  getImportV2Job(type: string, id: string): Observable<any> {
    return this.http.get<any>(this.integrationService + `/import-v2/${type}/job/${id}`);
  }

  commitImportV2(type: string, id: string, payload: { skip_invalid_rows?: boolean }): Observable<any> {
    return this.http.post<any>(this.integrationService + `/import-v2/${type}/job/${id}/commit`, payload);
  }

  cancelImportV2(type: string, id: string): Observable<any> {
    return this.http.post<any>(this.integrationService + `/import-v2/${type}/job/${id}/cancel`, {});
  }

  discardImportV2(type: string, id: string): Observable<any> {
    return this.http.delete<any>(this.integrationService + `/import-v2/${type}/job/${id}`);
  }

  pollImportV2Job(type: string, id: string): Observable<any> {
    return interval(2000).pipe(
      switchMap(() => this.getImportV2Job(type, id)),
      takeWhile((response) => {
        const s = response?.job?.status;
        return s !== 'SUCCESS' && s !== 'FAILED' && s !== 'PARTIAL_SUCCESS' && s !== 'CANCELLED';
      }, true),
      catchError((error) => throwError(error))
    );
  }

  pollImportJobStatus(id: string): Observable<any> {
    return interval(2000).pipe(
      switchMap(() => this.getImportJobStatus(id)),
      takeWhile(
        (response) =>
          response.importJob && response.importJob.status !== 'SUCCESS' && response.importJob.status !== 'FAILED',
        true
      ),
      catchError((error) => {
        return throwError(error);
      })
    );
  }
  getJobStatus(jobId: string): Observable<any> {
    // Replace with your API endpoint
    return this.http.get<any>(this.integrationService + `/background-job/job-id/${jobId}`);
  }

  pollJobStatus(jobId: string): Observable<any> {
    return interval(5000).pipe(
      switchMap(() => this.getJobStatus(jobId)),
      takeWhile((response) => response.status !== 'success', true),
      catchError((error) => {
        return of(null);
      })
    );
  }

  saveJobId(jobIdKey: any, jobId: string): void {
    localStorage.setItem(jobIdKey, jobId);
  }

  getSavedJobId(jobIdKey: any): string | null {
    return localStorage.getItem(jobIdKey);
  }

  clearSavedJobId(jobIdKey: any): void {
    localStorage.removeItem(jobIdKey);
  }
  getbackGroundJobList(payload: any, page: number, pageSize: number): Observable<any> {
    return this.http.post<any>(
      this.integrationService + `/background-job/get-job-list?page=${page}&pageSize=${pageSize}`,
      payload
    );
  }

  getReportById(jobId: any): Observable<any> {
    return this.http.get<any>(this.integrationService + `/background-job/job-id/${jobId}`);
  }

  downloadFile(url: any): Observable<any> {
    return this.http.get(url, { responseType: 'blob' });
  }

  // ─── Google Merchant Center APIs ─────────────────────────

  gmcTriggerSync(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/gmc/sync', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  gmcGetSyncStatus(shopId: string): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/gmc/sync/status?shopId=${shopId}`)
      .pipe(catchError((err) => throwError(err)));
  }

  gmcGetSyncHistory(shopId: string, page: number, pageSize: number): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/gmc/sync/history?shopId=${shopId}&page=${page}&pageSize=${pageSize}`)
      .pipe(catchError((err) => throwError(err)));
  }

  gmcSyncSingleProduct(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/gmc/sync-product', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  gmcRefreshStatusesByIds(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/gmc/refresh-statuses-by-ids', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  gmcRefreshStatuses(shopId: string): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/gmc/refresh-statuses?shopId=${shopId}`)
      .pipe(catchError((err) => throwError(err)));
  }

  gmcGetDashboard(shopId: string): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/gmc/dashboard?shopId=${shopId}`)
      .pipe(catchError((err) => throwError(err)));
  }

  gmcGetProducts(shopId: string, page: number, pageSize: number, status?: string): Observable<any> {
    let url = this.integrationService + `/gmc/products?shopId=${shopId}&page=${page}&pageSize=${pageSize}`;
    if (status) url += `&status=${status}`;
    return this.http.get<any>(url).pipe(catchError((err) => throwError(err)));
  }

  gmcExcludeProduct(shopId: string, variantId: string): Observable<any> {
    return this.http
      .put<any>(this.integrationService + `/gmc/products/${variantId}/exclude`, { shopId })
      .pipe(catchError((err) => throwError(err)));
  }

  gmcIncludeProduct(shopId: string, variantId: string): Observable<any> {
    return this.http
      .put<any>(this.integrationService + `/gmc/products/${variantId}/include`, { shopId })
      .pipe(catchError((err) => throwError(err)));
  }

  gmcBulkAction(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/gmc/products/bulk-action', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  gmcGetCategoryMappings(shopId: string): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/gmc/category-mappings?shopId=${shopId}`)
      .pipe(catchError((err) => throwError(err)));
  }

  gmcUpsertCategoryMapping(payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/gmc/category-mappings', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  gmcDeleteCategoryMapping(id: string): Observable<any> {
    return this.http
      .delete<any>(this.integrationService + `/gmc/category-mappings/${id}`)
      .pipe(catchError((err) => throwError(err)));
  }

  gmcGetGoogleTaxonomy(search?: string): Observable<any> {
    let url = this.integrationService + '/gmc/categories/google-taxonomy';
    if (search) url += `?search=${encodeURIComponent(search)}`;
    return this.http.get<any>(url).pipe(catchError((err) => throwError(err)));
  }

  gmcGetFeedSettings(shopId: string): Observable<any> {
    return this.http
      .get<any>(this.integrationService + `/gmc/settings?shopId=${shopId}`)
      .pipe(catchError((err) => throwError(err)));
  }

  gmcUpdateFeedSettings(payload: any): Observable<any> {
    return this.http
      .put<any>(this.integrationService + '/gmc/settings', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // ─── Order Analytics V2 APIs (Consolidated) ─────
  // NOTE: When order-service is deployed with new APIs, switch these to use
  // integrationService + '/order-analytics/...' for Redis caching via proxy

  private orderServiceUrl = environment.orderService + '/v1';

  orderDashboardV2(payload: any): Observable<any> {
    return this.http
      .post<any>(this.orderServiceUrl + '/analytics/order-dashboard', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  orderTrendsV2(payload: any): Observable<any> {
    return this.http
      .post<any>(this.orderServiceUrl + '/analytics/order-trends', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  orderCategoryProductsV2(payload: any): Observable<any> {
    return this.http
      .post<any>(this.orderServiceUrl + '/analytics/order-category-products', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // ─── Inventory Analytics V2 APIs (Consolidated) ─────
  // Hit inventory-service directly; the service stitches together stock state,
  // product-service variant prices, order-service velocity, and the ledger.

  private inventoryServiceUrl = environment.inventoryService + '/v1';

  inventoryDashboardV2(payload: any): Observable<any> {
    return this.http
      .post<any>(this.inventoryServiceUrl + '/analytics/inventory-dashboard', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  inventoryTrendsV2(payload: any): Observable<any> {
    return this.http
      .post<any>(this.inventoryServiceUrl + '/analytics/inventory-trends', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  inventoryDetailV2(payload: any): Observable<any> {
    return this.http
      .post<any>(this.inventoryServiceUrl + '/analytics/inventory-detail', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // ─── GA Dashboard APIs ───────────────────────────────────

  private gaPost(endpoint: string, payload: any): Observable<any> {
    return this.http
      .post<any>(this.integrationService + `/ga-dashboard/${endpoint}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  gaTrafficOverview(payload: any): Observable<any> {
    return this.gaPost('traffic-overview', payload);
  }
  gaTrafficByChannel(payload: any): Observable<any> {
    return this.gaPost('traffic-by-channel', payload);
  }
  gaTrafficBySourceMedium(payload: any): Observable<any> {
    return this.gaPost('traffic-by-source-medium', payload);
  }
  gaUsersTrend(payload: any): Observable<any> {
    return this.gaPost('users-trend', payload);
  }
  gaTrafficByDevice(payload: any): Observable<any> {
    return this.gaPost('traffic-by-device', payload);
  }
  gaTrafficByRegion(payload: any): Observable<any> {
    return this.gaPost('traffic-by-region', payload);
  }
  gaTrafficByOS(payload: any): Observable<any> {
    return this.gaPost('traffic-by-os', payload);
  }
  gaLandingPages(payload: any): Observable<any> {
    return this.gaPost('landing-pages', payload);
  }
  gaTopPages(payload: any): Observable<any> {
    return this.gaPost('top-pages', payload);
  }
  gaNewVsReturning(payload: any): Observable<any> {
    return this.gaPost('new-vs-returning', payload);
  }
  gaUserDemographics(payload: any): Observable<any> {
    return this.gaPost('user-demographics', payload);
  }
  gaConversionFunnel(payload: any): Observable<any> {
    return this.gaPost('conversion-funnel', payload);
  }
  gaFunnelRates(payload: any): Observable<any> {
    return this.gaPost('funnel-rates', payload);
  }
  gaConversionRate(payload: any): Observable<any> {
    return this.gaPost('conversion-rate', payload);
  }
  gaRevenueByChannel(payload: any): Observable<any> {
    return this.gaPost('revenue-by-channel', payload);
  }
  gaRealtime(payload: any): Observable<any> {
    return this.gaPost('realtime', payload);
  }
  gaBounceRateTrend(payload: any): Observable<any> {
    return this.gaPost('bounce-rate-trend', payload);
  }
  gaAiInsights(payload: any): Observable<any> {
    return this.gaPost('ai-insights', payload);
  }

  getScheduleSubscription(stripeScheduleId: string) {
    return this.http.get<any>(this.integrationService + `/subscription/schedule/${stripeScheduleId}`);
  }

  cancelSubscription(subscriptionId: string) {
    return this.http.get<any>(this.integrationService + `/subscription/cancel/${subscriptionId}`);
  }

  // ─── Reports module ────────────────────────────────────────────────
  // Catalog of all reports registered on the backend.
  getReportsRegistry(): Observable<{ reports: ReportMeta[]; byCategory: Record<string, ReportMeta[]> }> {
    return this.http
      .get<any>(this.integrationService + '/reports/registry')
      .pipe(catchError((err) => throwError(() => err)));
  }

  // Kick off a report generation job.
  runReport(payload: {
    reportKey: string;
    filters: Record<string, any>;
    format?: string;
    emailOnCompletion?: boolean;
    shopId?: string;
  }): Observable<any> {
    return this.http
      .post<any>(this.integrationService + '/reports/run', payload)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // User's job history — used by the My Exports drawer.
  listReportJobs(
    page = 1,
    pageSize = 20,
    reportKey?: string,
    shopId?: string
  ): Observable<{ jobs: ReportJob[]; total: number }> {
    const q = [`page=${page}`, `pageSize=${pageSize}`];
    if (reportKey) q.push(`reportKey=${encodeURIComponent(reportKey)}`);
    if (shopId) q.push(`shopId=${encodeURIComponent(shopId)}`);
    return this.http
      .get<any>(this.integrationService + `/reports/jobs?${q.join('&')}`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // Single job poll — used to refresh status in the drawer.
  getReportJob(jobId: string): Observable<{ job: ReportJob }> {
    return this.http
      .get<any>(this.integrationService + `/reports/jobs/${jobId}`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // Absolute URL for a job's token-gated download. The token IS the auth, so this
  // is safe to hand to window.open() (which cannot carry our auth headers).
  reportDownloadUrl(downloadPath: string): string {
    return this.integrationService + downloadPath;
  }

  /**
   * Open a stored export/import file.
   *
   * Every export button used to `window.open(res.path)` — the object's own URL in
   * the bucket. That only worked while the buckets served everything anonymously;
   * since they were narrowed to images and media, every *_EXPORT and
   * *_IMPORT_JOB prefix returns 403. So: swap the path for a short-lived
   * token-gated URL (authorised and tenant-checked server-side) and open that.
   *
   * Kept here rather than repeated in each component so there is one place that
   * knows downloads do not go straight to the bucket any more.
   */
  openStoredFile(path: string): void {
    if (!path) return;
    this.storedFileUrl(path).subscribe({
      next: (url) => {
        if (url) window.open(url, '_blank');
      }
    });
  }

  /**
   * The token-gated URL for a stored object, without opening it.
   *
   * `openStoredFile` is enough for a download button, but anything that RENDERS a
   * private object in-page — an <img>, or an iframe preview — needs the URL as a
   * value. A private object cannot be shown from its bucket URL at all, so a
   * viewer has to resolve it through here first.
   */
  storedFileUrl(path: string): Observable<string> {
    if (!path) return of('');
    return this.http.post<any>(this.integrationService + '/file/download-token', { path }).pipe(
      map((res) => (res?.downloadPath ? this.integrationService + res.downloadPath : '')),
      catchError((err) => throwError(() => err))
    );
  }
}
